/**
 * Gamma strike mapping and level helpers (GEX chart, First Glance).
 * ScannerData → GammaStrikeData uses the same logic as SpotGamma (stable + RTL).
 */

import type {
  GammaStrikeData,
  GammaLevel,
  GammaType,
  OptionDominance,
} from '../types/market.types';
import { computeSpotGammaGexMme, getUnderlyingPriceFallback } from './gammaCalculations';
import { normalizeSide } from './greeksUtils';
import type { DataMode } from '../types/DataMode';

/**
 * |GEX| at a strike must be >= this fraction of max |GEX| in the chain to tag
 * repulsive (GEX > 0) or magnetic (GEX < 0); else spot-γ sign → positive/negative (green/red).
 * Aligns First Glance / GEX chart semantics.
 */
export const GAMMA_GEX_STRUCTURE_FRACTION = 0.05;

export type GexStructureKind = 'magnetic' | 'repulsive' | 'neutral';

export function classifyGexStructure(gex: number, maxAbsGexInChain: number): GexStructureKind {
  if (!Number.isFinite(gex) || !Number.isFinite(maxAbsGexInChain) || maxAbsGexInChain <= 0) {
    return 'neutral';
  }
  const thresh = Math.max(maxAbsGexInChain * GAMMA_GEX_STRUCTURE_FRACTION, 1e-12);
  if (Math.abs(gex) < thresh) return 'neutral';
  return gex > 0 ? 'repulsive' : 'magnetic';
}

export function classifyGammaTypeFromGexAndSpot(
  spotGamma: number,
  gammaExposure: number,
  maxAbsGexInChain: number
): GammaType {
  const kind = classifyGexStructure(gammaExposure, maxAbsGexInChain);
  if (kind === 'repulsive') return 'repulsive';
  if (kind === 'magnetic') return 'magnetic';
  return spotGamma > 0 ? 'positive' : spotGamma < 0 ? 'negative' : 'positive';
}

/** Precompute gamma levels from gammaData (e.g. max spot gamma, flip zone, high exposure) */
export function computeGammaLevels(
  gammaData: GammaStrikeData[],
  currentPrice: number
): GammaLevel[] {
  if (!gammaData.length) return [];
  const sorted = [...gammaData].sort((a, b) => a.strike - b.strike);
  const levels: GammaLevel[] = [];
  let maxSpotGamma = 0;
  let maxSpotGammaPrice = currentPrice;
  let maxExposure = 0;
  let maxExposurePrice = currentPrice;
  let flipZonePrice = currentPrice;
  let flipZoneExposure = 0;

  for (const row of sorted) {
    const absSpot = Math.abs(row.spotGamma);
    if (absSpot > maxSpotGamma) {
      maxSpotGamma = absSpot;
      maxSpotGammaPrice = row.strike;
    }
    const absExp = Math.abs(row.gammaExposure);
    if (absExp > maxExposure) {
      maxExposure = absExp;
      maxExposurePrice = row.strike;
    }
  }

  const structureRows = sorted.filter(
    (r) => r.gammaType === 'repulsive' || r.gammaType === 'magnetic'
  );
  if (structureRows.length > 0) {
    const best = structureRows.reduce((a, b) => {
      const da = Math.abs(a.strike - currentPrice);
      const db = Math.abs(b.strike - currentPrice);
      if (Math.abs(da - db) > 1e-9) return da < db ? a : b;
      return Math.abs(b.gammaExposure) > Math.abs(a.gammaExposure) ? b : a;
    });
    flipZonePrice = best.strike;
    flipZoneExposure = best.gammaExposure;
  } else if (maxExposure > 0) {
    flipZonePrice = maxExposurePrice;
    flipZoneExposure = sorted.find((r) => r.strike === maxExposurePrice)?.gammaExposure ?? 0;
  }

  if (maxSpotGamma > 0) {
    levels.push({
      price: maxSpotGammaPrice,
      exposure: maxSpotGamma,
      label: `Max Spot Gamma - ${formatShortNum(maxSpotGamma)}`,
    });
  }
  levels.push({
    price: flipZonePrice,
    exposure: flipZoneExposure,
    label: `$${flipZonePrice.toFixed(2)} Flip Zone - ${formatShortNum(Math.abs(flipZoneExposure))}`,
  });
  if (maxExposure > 0) {
    levels.push({
      price: maxExposurePrice,
      exposure: maxExposure,
      label: `High Gamma Exposure - ${formatShortNum(maxExposure)}`,
    });
  }
  return levels;
}

function formatShortNum(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return n.toFixed(0);
}

/**
 * Convert ScannerData (stable / New RTL / Legacy RTL) to GammaStrikeData[] for GEX / dashboards.
 * Uses the same computeSpotGammaGexMme path as the main GEX tooling.
 */
export function scannerDataToGammaStrikeData(
  data: Record<string, unknown>[],
  underlyingSymbol: string,
  dataMode: DataMode = 'accumulated'
): GammaStrikeData[] {
  if (!data?.length) return [];
  const fallbackS = getUnderlyingPriceFallback(data, dataMode);
  const symbolUpper = (underlyingSymbol || '').trim().toUpperCase();
  const byStrike = new Map<
    number,
    { spotGamma: number; gammaExposure: number; callSpot: number; putSpot: number }
  >();

  for (const item of data) {
    const und = String((item as any).Underlying ?? (item as any).Symbol ?? '').trim().toUpperCase();
    if (symbolUpper && und !== symbolUpper) continue;
    const strike = Number((item as any).Strike ?? (item as any)['Strike Price'] ?? 0);
    if (!Number.isFinite(strike)) continue;

    const result = computeSpotGammaGexMme(item as any, dataMode, fallbackS);
    const side = normalizeSide(item as any);
    const cur = byStrike.get(strike) ?? {
      spotGamma: 0,
      gammaExposure: 0,
      callSpot: 0,
      putSpot: 0,
    };
    cur.spotGamma += result.spotGamma;
    cur.gammaExposure += result.gex;
    if (side === 'C') cur.callSpot += result.spotGamma;
    else if (side === 'P') cur.putSpot += result.spotGamma;
    byStrike.set(strike, cur);
  }

  const entries = Array.from(byStrike.entries());
  const maxAbsGex = entries.reduce(
    (m, [, agg]) => Math.max(m, Math.abs(agg.gammaExposure)),
    0
  );

  const out: GammaStrikeData[] = [];
  entries.forEach(([strike, agg]) => {
    const gammaType = classifyGammaTypeFromGexAndSpot(
      agg.spotGamma,
      agg.gammaExposure,
      maxAbsGex
    );
    const callAbs = Math.abs(agg.callSpot);
    const putAbs = Math.abs(agg.putSpot);
    let optionDominance: OptionDominance = 'mixed';
    if (callAbs > putAbs * 1.2) optionDominance = 'call_sold';
    else if (putAbs > callAbs * 1.2) optionDominance = 'put_sold';
    out.push({
      strike,
      spotGamma: agg.spotGamma,
      gammaExposure: agg.gammaExposure,
      gammaType,
      optionDominance,
    });
  });
  return out.sort((a, b) => a.strike - b.strike);
}
